import { VerifiedEmailHelp } from '../components/team/VerifiedEmailHelp';
import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useUnifiedEvent } from '../contexts/UnifiedEventContext';
import { teamService, TeamInvitation } from '../services/team.service';
import { Button } from '../components/shared/Button';
import { Alert } from '../components/shared/Alert';
export function TeamInvitationsPage() {
  const { t } = useTranslation('dashboard');
  const { currentUser, signOut, loading: authLoading } = useAuth();
  const { setActiveGenericEvent, allEvents } = useUnifiedEvent();
  const navigate = useNavigate();
  const location = useLocation();
  const [acceptedKey, setAcceptedKey] = useState('');
  useEffect(() => {
    setAcceptedKey('');
    setBusy('');
  }, [currentUser?.uid]);
  useEffect(() => {
    const accepted = allEvents.find(e => e.teamKey === acceptedKey);
    if (acceptedKey && accepted) {
      setActiveGenericEvent(accepted);
      navigate('/dashboard');
    }
  }, [acceptedKey, allEvents]);
  const [rows, setRows] = useState<TeamInvitation[]>([]),
    [loading, setLoading] = useState(false),
    [error, setError] = useState(''),
    [busy, setBusy] = useState(''),
    [revision, setRevision] = useState(0);
  useEffect(() => {
    let cancelled = false;
    setRows([]);
    setLoading(false);
    setError('');
    if (!currentUser?.emailVerified || currentUser.isAnonymous) return;
    setLoading(true);
    setError('');
    teamService
      .request<{ invitations: TeamInvitation[] }>('inbox')
      .then(data => {
        if (!cancelled) setRows(data.invitations);
      })
      .catch(() => {
        if (!cancelled) setError(t('team.loadError'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [currentUser?.uid, currentUser?.emailVerified, revision, t]);
  const accept = async (id: string) => {
    setBusy(id);
    setError('');
    try {
      const result = await teamService.request('accept', { invitationId: id });
      setAcceptedKey(result.event.teamKey);
      setRows(previous => previous.filter(i => i.id !== id));
    } catch {
      setError(t('team.acceptError'));
    } finally {
      setBusy('');
    }
  };
  return (
    <main className="max-w-2xl mx-auto p-6 space-y-5 text-gray-900 dark:text-white">
      <h1 className="text-2xl font-serif">{t('team.myInvitations')}</h1>
      <p>{t('team.identityHelp')}</p>
      {acceptedKey && <p role="status">{t('team.waiting')}</p>}
      {authLoading || loading ? (
        <p>{t('loading', { ns: 'common' })}</p>
      ) : !currentUser || currentUser.isAnonymous ? (
        <Link to="/login" state={{ from: location }} className="underline">
          {t('team.signIn')}
        </Link>
      ) : !currentUser.emailVerified ? (
        <VerifiedEmailHelp onVerified={() => setRevision(r => r + 1)} />
      ) : (
        <>
          <p className="text-sm break-all">{currentUser.email}</p>
          {error && (
            <Alert
              variant="error"
              description={error}
              action={<Button onClick={() => setRevision(r => r + 1)}>{t('team.retry')}</Button>}
            />
          )}{' '}
          {!error && !rows.length && <p>{t('team.noInvitations')}</p>}
          {rows.map(i => (
            <section key={i.id} className="border rounded-xl p-5 space-y-3">
              <h2 className="font-medium">{i.event.name}</h2>
              <p className="text-sm">
                {t(`team.${i.role}`)} · {i.modules.map(m => t(`team.${m}`)).join(', ')}
              </p>
              <Button disabled={Boolean(busy)} loading={busy === i.id} onClick={() => accept(i.id)}>
                {t('team.accept')}
              </Button>
            </section>
          ))}
        </>
      )}
      {currentUser && (
        <Button
          variant="outline"
          disabled={Boolean(busy)}
          onClick={async () => {
            try {
              await signOut();
              navigate('/login', { state: { from: location } });
            } catch {
              setError(t('team.actionError'));
            }
          }}
        >
          {t('team.switchAccount')}
        </Button>
      )}
      <Link to="/dashboard" className="block underline">
        {t('team.back')}
      </Link>
    </main>
  );
}
